/* @jsx h */
import { defineComponent, h, ref, VNode } from "vue";
import { BottomSheet } from "../BottomSheet";

export type PageHeaderProps = {
  title: string | VNode;
  /** Shown below the title on mobile only — e.g. active filter summary. */
  filterSummary?: string;
  /** Badge count shown on the mobile Filter button. */
  activeFilterCount?: number;
  /** Title shown in the mobile filter BottomSheet. Defaults to "Filters". */
  filterSheetTitle?: string;
};

/**
 * Sticky page header.
 *
 * Slots:
 *   default  — filter controls. Desktop: rendered inline on the right.
 *              Mobile: rendered inside a BottomSheet opened by the Filter button.
 *              Receives `{ closeMobileSheet }` as slot prop.
 *   actions  — always-visible action buttons (e.g. "New …"). Rendered on the
 *              right of the header on both desktop and mobile, never in the sheet.
 */
export const PageHeader = defineComponent<PageHeaderProps>({
  name: "PageHeader",
  props: ["title", "filterSummary", "activeFilterCount", "filterSheetTitle"],
  setup(props, { slots }) {
    const mobileOpen = ref(false);
    const closeMobileSheet = () => {
      mobileOpen.value = false;
    };

    return () => (
      <div class="shrink-0">
        <div class="flex items-center justify-between gap-2 px-3 xl:px-5 py-2.5 md:py-3 border-b border-base-content/10">
          {/* Left: title + mobile filter summary */}
          <div class="flex flex-col min-w-0 flex-1">
            {typeof props.title === "string" ? (
              <h1 class="text-xl md:text-2xl font-bold text-accent truncate">
                {props.title}
              </h1>
            ) : (
              props.title
            )}
            {props.filterSummary && (
              <p class="md:hidden text-xs text-base-content/60 mt-0.5 truncate">
                {props.filterSummary}
              </p>
            )}
          </div>

          {/* Right side */}
          <div class="flex items-center gap-2 shrink-0">
            {/* Desktop: filter controls inline */}
            {slots.default && (
              <div class="hidden md:block">
                {slots.default({ closeMobileSheet })}
              </div>
            )}

            {/* Mobile: Filter button */}
            {slots.default && (
              <button
                class="md:hidden btn btn-sm btn-ghost relative gap-1.5 pl-2 pr-3"
                onClick={() => {
                  mobileOpen.value = true;
                }}
              >
                <span class="material-symbols-outlined text-base">tune</span>
                <span class="text-sm font-medium">Filter</span>
                {(props.activeFilterCount ?? 0) > 0 && (
                  <span class="badge badge-primary badge-xs absolute -top-1 -right-1 min-w-[16px] h-4 text-[10px]">
                    {props.activeFilterCount}
                  </span>
                )}
              </button>
            )}

            {/* Actions slot — always visible, never in BottomSheet */}
            {slots.actions?.()}
          </div>
        </div>

        {/* Mobile filter BottomSheet */}
        {mobileOpen.value && (
          <BottomSheet
            isOpen={true}
            title={props.filterSheetTitle ?? "Filters"}
            onClose={closeMobileSheet}
          >
            {slots.default?.({ closeMobileSheet })}
          </BottomSheet>
        )}
      </div>
    );
  },
});
